/**
 * SessionTimeoutModal Component
 * 
 * A blocking warning modal that appears before session expiration.
 * Cannot be dismissed via overlay click or ESC key.
 * Only "Keep Browsing" or "Logout" can close it.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';

// =============================================================================
// Types
// =============================================================================

interface SessionTimeoutModalProps {
  isOpen: boolean;
  remainingSeconds: number;
  onKeepBrowsing: () => void;
  onLogout: () => void;
}

// =============================================================================
// Component
// =============================================================================

const SessionTimeoutModal: React.FC<SessionTimeoutModalProps> = ({
  isOpen,
  remainingSeconds,
  onKeepBrowsing,
  onLogout,
}) => {
  const [isKeepAliveLoading, setIsKeepAliveLoading] = useState(false);

  // Block ESC key from closing the modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen]);

  const handleKeepBrowsing = useCallback(async () => {
    setIsKeepAliveLoading(true);
    try {
      const { error } = await api.get('/api/auth/me');
      if (error && error.status === 401) {
        onLogout();
        return;
      }
      onKeepBrowsing();
    } catch {
      onLogout();
    } finally {
      setIsKeepAliveLoading(false);
    }
  }, [onKeepBrowsing, onLogout]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="session-timeout-modal-title"
      role="alertdialog"
      aria-modal="true"
      aria-describedby="session-timeout-modal-desc"
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay - NOT clickable (blocking modal) */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        />

        {/* Center modal vertically */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-xl text-right overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full border-t-4 border-amber-500">
          <div className="bg-white px-6 pt-6 pb-4">
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>

              {/* Content */}
              <div className="flex-1 text-right">
                <h3
                  className="text-lg font-bold text-gray-900"
                  id="session-timeout-modal-title"
                >
                  החיבור שלך עומד לפוג
                </h3>
                <p className="mt-2 text-sm text-gray-600" id="session-timeout-modal-desc">
                  מטעמי אבטחה, החיבור שלך יפוג בעוד{' '}
                  <span className="font-bold text-amber-700 text-base tabular-nums">
                    {remainingSeconds}
                  </span>{' '}
                  שניות.
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  לחץ &quot;המשך לגלוש&quot; כדי להישאר מחובר.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onLogout}
              disabled={isKeepAliveLoading}
              className="btn-secondary disabled:opacity-50"
            >
              התנתק
            </button>
            <button
              type="button"
              onClick={handleKeepBrowsing}
              disabled={isKeepAliveLoading}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 transition-colors bg-amber-600 hover:bg-amber-700 focus:ring-amber-500"
            >
              {isKeepAliveLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  מתחבר...
                </span>
              ) : (
                'המשך לגלוש'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutModal;
